import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Loader2, CheckCircle2, TrendingUp, ArrowLeft, CreditCard, RefreshCw, ShieldCheck, AlertCircle } from "lucide-react";

const costLabels = {
  bid_analysis:    "Bid/No-Bid analysis",
  proposal:        "Full proposal generation",
  knowledge_qa:    "Knowledge Q&A",
  rfp_analysis:    "RFP analyzer",
  estimate:        "Estimating agent",
  outreach_draft:  "Teaming intro email",
  nudge_draft:     "Follow-up nudge draft",
  autonomous_step: "Autonomous per-opp step",
};

export default function Credits() {
  const [state, setState] = useState(null);
  const [topup, setTopup] = useState(null);
  const [busy, setBusy] = useState(null);
  const [topupBusy, setTopupBusy] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();

  const load = async () => {
    try { const r = await api.get("/credits/status"); setState(r.data); } catch {}
    try { const r = await api.get("/credits/auto-topup"); setTopup(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);

  // If we're bouncing back from a Stripe pack payment, reconcile in the background.
  useEffect(() => {
    const sessionId = params.get("session_id");
    const kind = params.get("kind");
    if (sessionId && kind === "credit_pack") {
      (async () => {
        for (let i = 0; i < 5; i++) {
          try {
            await api.post(`/credits/reconcile/${sessionId}`);
            await load();
            toast.success("Credits added to your workspace");
            return;
          } catch { await new Promise(r => setTimeout(r, 1500)); }
        }
      })();
    }
    if (params.get("cancelled")) toast.info("Purchase cancelled — no charge made");
    if (params.get("setup") === "1") {
      // Card just saved via Stripe Checkout setup mode — reload state a few times
      // in case the webhook hasn't landed yet.
      (async () => {
        for (let i = 0; i < 5; i++) {
          await load();
          if ((topup && topup.has_payment_method) || i === 4) break;
          await new Promise(r => setTimeout(r, 1500));
        }
        toast.success("Payment method saved · Auto top-up is now armed");
      })();
    }
    if (params.get("setup") === "cancelled") toast.info("Card setup cancelled");
  }, [params]);

  const buy = async (packKey) => {
    setBusy(packKey);
    try {
      const r = await api.post("/credits/checkout", { pack_key: packKey, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const startAutoTopupSetup = async () => {
    setTopupBusy(true);
    try {
      const r = await api.post("/credits/auto-topup/setup", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed");
      setTopupBusy(false);
    }
  };

  const toggleAutoTopup = async (nextEnabled) => {
    setTopupBusy(true);
    try {
      const r = await api.post("/credits/auto-topup/toggle", { enabled: nextEnabled });
      setTopup((t) => ({ ...t, enabled: r.data.enabled }));
      toast.success(nextEnabled ? "Auto top-up armed" : "Auto top-up paused");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update auto top-up");
    } finally { setTopupBusy(false); }
  };

  const removeCard = async () => {
    if (!window.confirm("Remove saved card and disable auto top-up?")) return;
    setTopupBusy(true);
    try {
      await api.post("/credits/auto-topup/remove", {});
      await load();
      toast.success("Card removed · Auto top-up disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove card");
    } finally { setTopupBusy(false); }
  };

  const exhausted = params.get("reason") === "exhausted";

  return (
    <div className="p-8 stagger-in max-w-6xl" data-testid="credits-page">
      <Link to="/dashboard" className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-violet-600 flex items-center gap-2 mb-6" data-testid="credits-back"><ArrowLeft className="w-3.5 h-3.5" />BACK</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="bf-eyebrow inline-block text-amber-600 mb-3">AI CREDITS</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight text-slate-900">Fuel <span className="bf-grad-text-warm">the agents</span>.</h1>
          <p className="text-slate-500 mt-3 max-w-2xl">Every AI action — bid analysis, proposals, drafts, estimates — pulls from your monthly credit allotment. Top up any time.</p>
        </div>
      </div>

      {exhausted && (
        <div className="mb-6 rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50 p-5 flex items-start gap-3" data-testid="credits-exhausted-banner">
          <Zap className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-slate-900">Out of credits</div>
            <div className="text-sm text-slate-600">Grab a pack below to keep drafting proposals and running the agents this month.</div>
          </div>
        </div>
      )}

      {state && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-8" data-testid="credits-summary">
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">CURRENT PLAN</div>
              <div className="font-display font-bold text-2xl text-slate-900 capitalize">{state.plan}</div>
              <div className="text-xs text-slate-500 mt-1">{state.included_monthly?.toLocaleString()} credits / month included</div>
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">REMAINING THIS MONTH</div>
              <div className={`font-display font-black text-3xl ${state.remaining_total < 20 ? "text-rose-600" : state.remaining_total < 100 ? "text-amber-600" : "text-emerald-600"}`} data-testid="credits-remaining">
                {(state.remaining_total ?? 0).toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-1">{state.remaining_included?.toLocaleString()} included + {state.addon_balance?.toLocaleString()} add-on</div>
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">USED THIS MONTH</div>
              <div className="font-display font-bold text-2xl text-slate-900">{(state.used_this_month ?? 0).toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">Resets on the 1st</div>
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">ADD-ON BALANCE</div>
              <div className="font-display font-bold text-2xl text-slate-900">{(state.addon_balance ?? 0).toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">Never expires</div>
            </div>
          </div>
        </div>
      )}

      {/* AUTO TOP-UP */}
      {topup && (
        <div
          className={`rounded-2xl border p-6 mb-8 transition-colors ${
            topup.enabled
              ? "border-emerald-200 bg-gradient-to-br from-emerald-50/70 via-white to-white"
              : "border-slate-200 bg-white"
          }`}
          data-testid="auto-topup-card"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${topup.enabled ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                <RefreshCw className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold text-xl text-slate-900">Auto top-up</div>
                  {topup.enabled ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-widest text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5" data-testid="auto-topup-badge-on">
                      <ShieldCheck className="w-3 h-3" /> ARMED
                    </span>
                  ) : topup.has_payment_method ? (
                    <span className="text-[10px] font-mono font-bold tracking-widest text-slate-500 bg-slate-100 rounded-full px-2 py-0.5" data-testid="auto-topup-badge-paused">PAUSED</span>
                  ) : (
                    <span className="text-[10px] font-mono font-bold tracking-widest text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid="auto-topup-badge-setup">SET UP TO ARM</span>
                  )}
                </div>
                <div className="text-sm text-slate-600 mt-1 max-w-xl">
                  Automatically buy <strong>{topup.pack_credits} credits</strong> for <strong>${(topup.pack_amount_cents / 100).toFixed(0)}</strong> whenever your balance drops below <strong>{topup.threshold}</strong>. The agents never stop mid-run.
                </div>
                {topup.has_payment_method && (
                  <div className="text-xs text-slate-500 mt-2 flex items-center gap-2" data-testid="auto-topup-card-summary">
                    <CreditCard className="w-3.5 h-3.5" />
                    <span className="uppercase">{topup.pm_brand || "card"}</span>
                    <span>····</span>
                    <span>{topup.pm_last4 || "----"}</span>
                    {topup.pm_exp_month && topup.pm_exp_year && (
                      <span className="text-slate-400">· exp {String(topup.pm_exp_month).padStart(2, "0")}/{String(topup.pm_exp_year).slice(-2)}</span>
                    )}
                  </div>
                )}
                {topup.last_topup_status === "succeeded" && topup.last_topup_at && (
                  <div className="text-[11px] text-emerald-600 mt-2" data-testid="auto-topup-last-ok">
                    Last refill: {new Date(topup.last_topup_at).toLocaleString()} · +{topup.pack_credits} credits
                  </div>
                )}
                {topup.last_topup_status === "failed" && (
                  <div className="text-[11px] text-rose-600 mt-2 flex items-center gap-1.5" data-testid="auto-topup-last-fail">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Last refill failed: {topup.last_topup_error || "card declined"} — please update your card
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!topup.has_payment_method ? (
                <button
                  onClick={startAutoTopupSetup}
                  disabled={topupBusy}
                  className="bf-pill text-xs px-5 py-2.5 flex items-center gap-2 disabled:opacity-50"
                  data-testid="auto-topup-setup-btn"
                >
                  {topupBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />REDIRECTING</> : <><CreditCard className="w-3.5 h-3.5" />SET UP CARD</>}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => toggleAutoTopup(!topup.enabled)}
                    disabled={topupBusy}
                    className={`text-xs px-5 py-2.5 rounded-full font-display font-bold uppercase tracking-wider transition-all flex items-center gap-2 disabled:opacity-50 ${
                      topup.enabled
                        ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        : "bf-pill"
                    }`}
                    data-testid="auto-topup-toggle-btn"
                  >
                    {topupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (topup.enabled ? "Pause" : "Enable")}
                  </button>
                  <button
                    onClick={removeCard}
                    disabled={topupBusy}
                    className="text-[11px] font-mono font-bold tracking-widest text-slate-500 hover:text-rose-600 uppercase px-3 py-2 disabled:opacity-50"
                    data-testid="auto-topup-remove-btn"
                  >
                    Remove card
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6 mb-10">
        {(state?.packs || []).map((p, i) => {
          const isBest = i === 1;
          const isBiggest = i === 2;
          const perCreditCents = (p.amount_cents / p.credits).toFixed(1);
          return (
            <div key={p.key} className={`rounded-2xl p-8 flex flex-col bg-white border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${isBest ? "border-violet-300 shadow-[0_20px_60px_-15px_rgba(139,92,246,0.35)] ring-2 ring-violet-200/40" : isBiggest ? "border-emerald-300" : "border-slate-200"}`} data-testid={`pack-${p.key}`}>
              {isBest && <div className="inline-flex self-start bf-pill text-[10px] px-3 py-1 mb-4 items-center gap-1"><TrendingUp className="w-3 h-3" />POPULAR</div>}
              {isBiggest && <div className="inline-flex self-start text-[10px] px-3 py-1 mb-4 items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 font-mono font-bold tracking-widest">BEST VALUE</div>}
              {!isBest && !isBiggest && <div className="text-[10px] font-mono font-bold tracking-widest text-sky-600 mb-4">STARTER PACK</div>}
              <div className="font-display font-black text-3xl text-slate-900 mb-2">{p.credits.toLocaleString()} credits</div>
              <div className="mb-4">
                <div className="flex items-baseline">
                  <span className="font-display font-black text-4xl text-slate-900">${(p.amount_cents / 100).toFixed(0)}</span>
                  <span className="text-slate-500 ml-2 text-sm">one-time</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">≈ {perCreditCents}¢ per credit</div>
              </div>
              <div className="text-sm text-slate-600 mb-6 space-y-1.5">
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{p.credits.toLocaleString()} AI actions</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{Math.floor(p.credits / 3).toLocaleString()} full proposals</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" />Never expires</div>
              </div>
              <button onClick={() => buy(p.key)} disabled={busy === p.key} className={`text-center py-3 rounded-full font-display font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-auto ${isBest ? "bf-pill" : "bf-pill-ghost hover:!border-violet-400 hover:!text-violet-600"}`} data-testid={`buy-${p.key}`}>
                {busy === p.key ? <><Loader2 className="w-4 h-4 animate-spin" />REDIRECTING</> : "BUY NOW"}
              </button>
            </div>
          );
        })}
      </div>

      {state && (
        <div className="rounded-2xl bg-white border border-slate-200 p-6" data-testid="credits-costs">
          <div className="text-[10px] font-mono font-bold tracking-widest text-fuchsia-600 mb-4">WHAT EACH ACTION COSTS</div>
          <div className="grid md:grid-cols-2 gap-y-2 gap-x-8">
            {Object.entries(state.costs || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-b-0">
                <span className="text-slate-700">{costLabels[k] || k}</span>
                <span className="font-mono font-bold text-violet-600">{v} credit{v === 1 ? "" : "s"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
